import React from "react";
import {
  createStyles,
  Box,
  Stack,
  Text,
  Center,
  Image,
  Space,
  Group,
  Anchor,
} from "@mantine/core";
import {
  IconMusic,
  IconHeadphones,
  IconSparkles,
  IconWaveSine,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";

function FloatingIcon({ icon: Icon, size, color, top, left, right, rotate, opacity }) {
  return (
    <Box
      sx={{
        position: "absolute",
        top,
        left,
        right,
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        opacity: opacity || 0.15,
        pointerEvents: "none",
        zIndex: 0,
        "@media (max-width: 768px)": {
          display: "none",
        },
      }}
    >
      <Icon size={size || 40} color={color} stroke={1.5} />
    </Box>
  );
}

function HomePage({ children, ...otherProps }) {
  const { classes } = useStyles();
  const navigation = useNavigate();
  return (
    <Box className={classes.pageWrapper} {...otherProps}>
      {/* Decorative Icons — left side */}
      <FloatingIcon icon={IconMusic} size={60} color="#B049C5" top="4%" left="4%" rotate={-15} opacity={0.15} />
      <FloatingIcon icon={IconSparkles} size={38} color="#FFB347" top="15%" left="2%" rotate={20} opacity={0.18} />
      <FloatingIcon icon={IconHeadphones} size={48} color="#E63230" top="30%" left="3%" rotate={12} opacity={0.13} />
      <FloatingIcon icon={IconSparkles} size={34} color="#FF85A2" top="45%" left="5%" rotate={5} opacity={0.16} />
      <FloatingIcon icon={IconMusic} size={44} color="#6A59D8" top="60%" left="2%" rotate={-25} opacity={0.14} />
      <FloatingIcon icon={IconSparkles} size={42} color="#B049C5" top="75%" left="4%" rotate={30} opacity={0.15} />
      <FloatingIcon icon={IconWaveSine} size={50} color="#FF6B9D" top="88%" left="3%" rotate={-8} opacity={0.13} />

      {/* Decorative Icons — right side */}
      <FloatingIcon icon={IconHeadphones} size={52} color="#6A59D8" top="6%" right="5%" rotate={10} opacity={0.13} />
      <FloatingIcon icon={IconWaveSine} size={56} color="#F472B6" top="20%" right="3%" rotate={-10} opacity={0.15} />
      <FloatingIcon icon={IconMusic} size={50} color="#B049C5" top="35%" right="4%" rotate={-20} opacity={0.14} />
      <FloatingIcon icon={IconWaveSine} size={58} color="#6A59D8" top="50%" right="3%" rotate={25} opacity={0.13} />
      <FloatingIcon icon={IconHeadphones} size={54} color="#F472B6" top="65%" right="5%" rotate={-5} opacity={0.14} />
      <FloatingIcon icon={IconWaveSine} size={46} color="#B049C5" top="80%" right="4%" rotate={-15} opacity={0.16} />

      {/* Hero Section */}
      <Box className={classes.root}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center px={50} pt={30} pb={10}>
              <Stack spacing={0} justify="center">
                <Center>
                  <Group spacing={10} align="center" sx={{ cursor: "pointer" }} onClick={() => navigation("/")}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, #B049C5, #6A59D8)",
                      }}
                    >
                      <Image
                        src="/flipwave-ico.svg"
                        alt="FlipWave"
                        width={30}
                        height={30}
                        sx={{ objectFit: "contain" }}
                      />
                    </Box>
                    <Text
                      weight={800}
                      size={30}
                      align="center"
                      sx={{ color: "#7749FD" }}
                    >
                      FlipWave
                    </Text>
                  </Group>
                </Center>

                <Text
                  weight={900}
                  size={46}
                  align="center"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "28px",
                      lineHeight: "2.1rem",
                    },
                  }}
                >
                  Reverse Audio Play
                </Text>
                <Text
                  weight={400}
                  size={20}
                  align="center"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Discover the fun of flipping sound backwards!
                </Text>
              </Stack>
            </Center>
          </Box>

          {/* Hero Image + CTA */}
          <Box className={classes.section}>
            <Box
              sx={{
                background: "linear-gradient(135deg, #B049C5, #6A59D8)",
                borderRadius: 36,
                padding: 24,
              }}
            >
              <Image
                src="/images/321_1x_shots_so.webp"
                alt="FlipWave app preview"
                radius={24}
              />
            </Box>
            <Center mt={20}>
              <Anchor href="#" target="_blank">
                <Image
                  src="/images/app-store-button-dark.svg"
                  width={200}
                  alt="Download on the App Store"
                />
              </Anchor>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      {/* Feature 1: Record with One Tap */}
      <Box sx={{ width: "100%", backgroundColor: "#FFFFFF", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "2rem",
                    },
                  }}
                >
                  Record with One Tap
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Capture your voice or any sound instantly.
                </Text>
              </Stack>
              <Image src="/images/116_1x_shots_so.webp" alt="One tap recording" />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 2: Play, Reverse & Share */}
      <Box sx={{ width: "100%", backgroundColor: "#6A59D8", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Image
                src="/images/94_1x_shots_so.webp"
                alt="Play and reverse"
              />
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#FFFFFF",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Play, Reverse & Share
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Listen normally, flip it backwards. Share it and challenge your
                  friends.
                </Text>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 3: Edit and Trim */}
      <Box sx={{ width: "100%", backgroundColor: "#FFFFFF", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Edit and Trim
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Fine-tune your recordings with waveform precision
                </Text>
              </Stack>
              <Image
                src="/images/389_1x_shots_so.webp"
                alt="Edit and trim"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 4: Export & Share */}
      <Box sx={{ width: "100%", backgroundColor: "#6A59D8", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Image
                src="/images/647_1x_shots_so.webp"
                alt="Export and share"
                sx={{ maxWidth: 500 }}
              />
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#FFFFFF",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Export & Share
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Export in M4A and share to any social media
                </Text>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Light & Dark Mode + Final CTA */}
      <Box className={classes.root} sx={{ position: "relative", zIndex: 1 }}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center p={50}>
              <Stack spacing={0} justify="center">
                <Text
                  weight={900}
                  size={36}
                  align="center"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.7rem",
                      lineHeight: "2rem",
                    },
                  }}
                >
                  Light & Dark Mode
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="center"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Optimized for comfortable use in any environment
                </Text>
              </Stack>
            </Center>
          </Box>
          <Box className={classes.section}>
            <Image
              src="/images/730_1x_shots_so.webp"
              alt="Light and dark mode"
              radius={36}
            />
            <Center mt={20}>
              <Anchor href="#" target="_blank">
                <Image
                  src="/images/app-store-button-dark.svg"
                  width={200}
                  alt="Download on the App Store"
                />
              </Anchor>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.root} sx={{ position: "relative", zIndex: 1 }}>
        <Box className={classes.grid} mt={30}>
          <Box className={classes.sectionTwo}>
            <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
              Made with love by Anatoli Niculeac<sup>&copy;</sup> 2026
            </Text>
            <Group spacing={10} p={20} position="right">
              <Anchor component={Link} to={"/support"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#7749FD" }}>
                  Support
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/terms"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#7749FD" }}>
                  Terms of use
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/privacy"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#7749FD" }}>
                  Privacy Policy
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/contact"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#7749FD" }}>
                  Contact
                </Text>
              </Anchor>
            </Group>
            <Space h={10} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default HomePage;

const useStyles = createStyles((theme) => ({
  pageWrapper: {
    width: "100%",
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 15% 20%, rgba(176, 73, 197, 0.08) 0%, transparent 50%), " +
      "radial-gradient(ellipse at 85% 40%, rgba(106, 89, 216, 0.08) 0%, transparent 50%), " +
      "radial-gradient(ellipse at 20% 70%, rgba(106, 89, 216, 0.06) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 80% 85%, rgba(176, 73, 197, 0.06) 0%, transparent 45%), " +
      "linear-gradient(180deg, #FBE1F6 0%, #FCE9F9 100%)",
    position: "relative",
    overflow: "hidden",
  },

  root: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },

  section: {
    maxWidth: 980,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 15,
    paddingRight: 15,
  },

  sectionTwo: {
    maxWidth: 980,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 15,
    paddingRight: 15,
  },

  grid: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateColumns: "auto",
    gridTemplateRows: "auto",
    "@media (max-width: 1023px)": {
      gridTemplateColumns: "auto",
      justifyContent: "stretch",
    },
    "@media (min-width: 1600px)": {
      gridTemplateColumns: "auto",
      gridTemplateRows: "auto",
      WebkitTransition: "300ms",
    },
  },

  link: {
    whiteSpace: "nowrap",
    textDecoration: "none",
    ":last-child": {
      borderRight: "none",
    },
    webkitTransition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    transition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    ":hover": {
      transition: "0.5s color",
      textDecoration: "none",
      opacity: 0.7,
    },
  },
}));
